import React, { useCallback, useEffect, useState, useRef } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row, Tab, Tabs, Spinner } from "react-bootstrap";
import { BsDownload } from "react-icons/bs";
import { GoAlert } from "react-icons/go";
import { VscReplace } from "react-icons/vsc";
import { BiEdit } from "react-icons/bi";
import DOMPurify from "dompurify";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ReactTooltip from "react-tooltip";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import CheckBoxProcedimentosFormik from "../../components/UI/Checkbox/CheckBoxProcedimentosFormik";
import DateTimeInputFormik from "../../components/UI/DateTimeInput/DateTimeInputFormik";
import FormAlert from "../../components/UI/FormAlert/FormAlert";
import HtmlEditor from "../../components/UI/HtmlEditorInput/HtmlEditor";
import OverlayInfoAdicional from "../../components/UI/Overlays/OverlayInfoAdicional";
import SelectFileInput from "../../components/UI/SelectFileInput/SelectFileInput";
import SelectInputCachedFormik from "../../components/UI/SelectInput/SelectInputCachedFormik";
import TextInputFormik from "../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../components/UI/Spinner/TextSpinner";
import funcionalidades from "../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import ListaContribuinteAcaoFiscal from "./ListaContribuinteAcaoFiscal";
import AnexosSpf from "./components/AnexosSpf";
import FileDownloader from "../../components/UI/Button/FileDownloader";
import { RetornaDataToNumero } from "../../components/Formatters/DateTime";
import ButtonImagePermission from "../../components/UI/Button/ButtonImagePermission";
import useDocumentoParteServico from "../OS/ParteServico/hooks/useDocumentoParteServico";
import useBuscaTextoPadraoSpf from "./hooks/useBuscaTextoPadraoSpf";
import EfisModal from "../../components/UI/EfisModal/EfisModal";
import ReplaceChar from "../../components/Formatters/ReplaceChar";

const validationSchema = Yup.object().shape({
  numeroAberturaProcesso: Yup.string().default(""),
  sequencialOrigem: Yup.string().default(""),
  tipoOrigem: Yup.string().default(""),
  sequencialHomologador: Yup.string()
    .default("")
    .required("Informe o homologador")
    .min(1, "Nome muito curto")
    .max(100, "Nome muito longo"),
  sequencialTipoAcao: Yup.string().default("").required("Informe o Tipo de Ação Fiscal"),
  sequencialCiclo: Yup.string().default("").required("Informe o Ciclo"),
  sequencialSetor: Yup.string().default("").required("Informe o setor"),
  dataInicialFiscalizacao: Yup.date().min(new Date(1900, 1, 1), "Data inicial inválida."),
  dataFinalFiscalizacao: Yup.date().min(new Date(1900, 1, 1), "Data final inválida."),
  procedimentos: Yup.array().default([]),
  contribuintes: Yup.array()
    .default([])
    .test("qtd-contribuinte", "Selecione pelo menos 1 contribuinte", (value) => (value || []).length > 0),
  anexos: Yup.array()
    .default([])
    .test(
      "integrity-anexos",
      "Verifique os arquivos em que consta erro. Remova e tente a seleção novamente. ",
      (value) => {
        // Verificar se uploads estão OK e se não há críticas
        for (let i = 0; i < (value || []).length; i += 1) if (value[i].error || value[i].uploading) return false;

        return true;
      },
    ),
  numeroSei: Yup.string()
    .default("")
    .when("tipoOrigem", { is: "S", then: Yup.string().required("Informe o número SEI") }),
  textoPadrao: Yup.string().default("").required("Informe o texto da SPF"),
  textoComplementar: Yup.string().default(""),
});

const initialValues = Object.fromEntries(
  Object.keys(validationSchema.fields).map((key) => [
    key,
    ["procedimentos", "contribuintes", "anexos"].includes(key) ? [] : "",
  ]),
);

const documentoParteServico = { exibir: () => {} };
const documentoDenuncia = { exibir: () => {} };
const documentoCobranca = { exibir: () => {} };

const exibirDocumento = (origem, sequencial) => {
  if (origem === "P") {
    documentoParteServico.exibir(sequencial);
  } else if (origem === "D") {
    documentoDenuncia.exibir(sequencial);
  } else {
    documentoCobranca.exibir(sequencial);
  }
};

const FormProcedimentoFiscal = ({ history, location }) => {
  const { state } = location;
  const [documentoContribuinte, setDocumentoContribuinte] = useState("");
  const [tipoOrigem, setTipoOrigem] = useState("");
  const [anexos, setAnexos] = useState([]);
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [ehParteServico, setEhParteServico] = useState(false);
  const [isPeriodo, setPeriodo] = useState(false);
  const { setDocParams } = useDocumentoParteServico();
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();
  const { request: requestAnexo, data: dataAnexo, errors: errorsAnexo, isLoading: isloadingAnexo } = useApiClient();
  const { request: requestDownload, data: dataDownload } = useApiClient();
  const hasSetValues = useRef(false);

  documentoParteServico.exibir = (sqParteServico) => {
    setDocParams({ sqParteServico });
  };

  const handleSubmit = useCallback(
    (values) => {
      const params = {
        ...values,
        textoComplementar: ReplaceChar(values.textoComplementar),
        textoPadrao: ReplaceChar(values.textoPadrao),
      };
      if (RetornaDataToNumero(values.dataInicialFiscalizacao) > RetornaDataToNumero(values.dataFinalFiscalizacao)) {
        setPeriodo(false);
        toast.warning("Data inicial não pode ser maior que a data final");
      } else if (values.numeroAberturaProcesso) {
        requestForm(apiRoutes.fiscalizacao.procedimentoFiscal.alterarSpfRevisao, params);
      } else {
        requestForm(apiRoutes.fiscalizacao.procedimentoFiscal.inserirSpf, params);
      }
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, setValues, values, isSubmitting, isValid, errors } = formik;

  const [showTextoSpf, setShowTextoSpf] = useState(true);
  const [showViewSubstituicao, setShowViewSubstituicao] = useState(false);
  const [textoBody, setTextoBody] = useState("");

  const { textoPadrao } = useBuscaTextoPadraoSpf({ formik });

  const { push } = history;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) setSubmitting(false);
    else setSubmitting(isPeriodo);

    if (errorForm && isValid && !isLoading) setErrors({ ...errors, ...errorForm });

    if (errors && isSubmitting) {
      if (errors.numeroSei) {
        toast.warning(errors.numeroSei);
      }
      if (errors.sequencialHomologador) {
        toast.warning(errors.sequencialHomologador);
      }
      if (errors.sequencialSetor) {
        toast.warning(errors.sequencialSetor);
      }
      if (errors.sequencialTipoAcao) {
        toast.warning(errors.sequencialTipoAcao);
      }
      if (errors.dataInicialFiscalizacao) {
        toast.warning(errors.dataInicialFiscalizacao);
      }
      if (errors.dataFinalFiscalizacao) {
        toast.warning(errors.dataFinalFiscalizacao);
      }
      if (errors.textoComplementar) {
        toast.warning(errors.textoComplementar);
      }
      if (errors.textoPadrao) {
        toast.warning(errors.textoPadrao);
      }
      if (errors.contribuintes) {
        toast.warning(errors.contribuintes);
      }
      if (errors.sequencialCiclo) {
        toast.warning(errors.sequencialCiclo);
      }
    }

    if (dataForm) {
      toast.success(dataForm.result);
      push("/app/spf/pendencias");
    }
  }, [setSubmitting, setErrors, push, errors, isSubmitting, isValid, dataForm, errorForm, isLoading, isPeriodo]);

  useEffect(() => {
    if (state?.tipoOrigem === "P" || values.tipoOrigem === "P") {
      setEhParteServico(true);
    } else {
      setEhParteServico(false);
    }
  }, [state, values]);

  useEffect(() => {
    setValues((curr) => ({
      ...curr,
      // sequencialOrigem: state.sequencialOrigem,
      // tipoOrigem: state.tipoOrigem,
    }));
    if (state.documento) {
      setDocumentoContribuinte(state.documento);
    }
  }, [state, setValues]);

  // Carregar item
  const {
    request: consultaFormRequest,
    data: consultaFormData,
    errors: consultaFormErrors,
    isLoading: consultaFormIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state?.itemKey) {
      consultaFormRequest(apiRoutes.fiscalizacao.procedimentoFiscal.consultaSpfRevisao, { id: state.itemKey });
      requestAnexo(apiRoutes.fiscalizacao.procedimentoFiscal.consultaAnexoSpf, { sqAberturaProcesso: state.itemKey });
      // delete state.itemKey;
    }
  }, [consultaFormRequest, requestAnexo, state]);

  useEffect(() => {
    if (dataAnexo) {
      setAnexos(dataAnexo.result);
    }
    if (errorsAnexo) {
      toast.error(errorsAnexo.message);
    }
  }, [dataAnexo, errorsAnexo]);

  useEffect(() => {
    if ((!consultaFormData && !consultaFormErrors) || consultaFormIsLoading || hasSetValues.current) return;

    if (consultaFormData && consultaFormData.result.length) {
      const data = consultaFormData.result[0];
      setValues({
        ...data,
        anexos: [],
        tipoOrigem: data.tipoOrigem ?? "",
        sequencialOrigem: data.sequencialOrigem ?? "",
        textoPadrao: textoPadrao,
        numeroSei: data.numeroSei ?? "",
      });

      setDocumentoContribuinte(data.numeroIe || data.numeroCnpjCpf);
      hasSetValues.current = true; // marca que já rodou
    }

    if (consultaFormErrors) {
      toast.error(consultaFormErrors.message);
    }
  }, [consultaFormData, consultaFormErrors, consultaFormIsLoading, setValues, textoPadrao, state]);

  useEffect(() => {
    if (state.tipoOrigem === "P" || consultaFormData?.tipoOrigem === "P") {
      setTipoOrigem("- Parte de Serviço");
      setNomeArquivo(`parte-servico-${state.sequencialOrigem}.pdf`);
      const paramsParte = { numParteServico: state.sequencialOrigem };
      requestDownload(apiRoutes.fiscalizacao.parteservico.listaParteServico, paramsParte);
    } else if (state.tipoOrigem === "C" || consultaFormData?.tipoOrigem === "C") {
      setTipoOrigem("- Cobrança");
      setNomeArquivo(`ResultadoCobranca-${state.sequencialOrigem}.html`);
      const paramsCobranca = { sqCobranca: state.sequencialOrigem };
      requestDownload(apiRoutes.fiscalizacao.procedimentoFiscal.downloadCobranca, paramsCobranca);
    } else if (state.tipoOrigem === "S" || consultaFormData?.tipoOrigem === "S") {
      setTipoOrigem("- Processo SEI");
    } else if (state.tipoOrigem === "D" || consultaFormData?.tipoOrigem === "D") {
      setTipoOrigem("- Denúncia");
      setNomeArquivo(`denuncia-${state.sequencialOrigem}.pdf`);
      const paramsDenuncia = { sqDenuncia: state.sequencialOrigem };
      requestDownload(apiRoutes.fiscalizacao.procedimentoFiscal.downloadDenuncia, paramsDenuncia);
    } else {
      setTipoOrigem("- Individual/Lote");
    }
  }, [requestDownload, state, consultaFormData]);

  let labelProcedimento;
  if (values.tipoOrigem === "D" || state?.tipoOrigem === "D") {
    labelProcedimento = "Denúncia";
  } else if (values.tipoOrigem === "C" || state?.tipoOrigem === "C") {
    labelProcedimento = "Cobrança";
  } else if (values.tipoOrigem === "P" || state?.tipoOrigem === "P") {
    labelProcedimento = "Parte de Serviço";
  } else {
    labelProcedimento = "SPF";
  }

  useEffect(() => {
    if (!consultaFormData || textoPadrao) {
      setValues((curr) => ({
        ...curr,
        // sequencialOrigem: state.sequencialOrigem,
        // tipoOrigem: state.tipoOrigem,
        textoPadrao: textoPadrao,
      }));
    }
  }, [setValues, textoPadrao, state, consultaFormData]);

  return (
    <>
      <EfisModal
        show={showViewSubstituicao}
        title="Texto da OS"
        size="lg"
        closeHandler={() => setShowViewSubstituicao(false)}
        primaryLabelVisible={false}
        secondaryLabel="Fechar"
        body={<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textoBody) }} />}
      />
      {/* Cabeçalho da Página */}
      <h1 className="h3 mb-4 text-gray-800">Solicitação de Procedimento Fiscal</h1>
      <div className="row">
        <div className="col-lg-12">
          {/* Conteúdo personalizado (card) */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {values.numeroAberturaProcesso
                      ? `Alteração de SPF Nº ${values.numeroAberturaProcesso}`
                      : `Inclusão de SPF ${tipoOrigem}`}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={values} keyFieldName="numeroAberturaProcesso" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={dataForm}
                errors={errorForm}
                isLoading={isLoading || consultaFormIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Tabs defaultActiveKey="geral" transition={false} id="tabSpf">
                  <Tab
                    eventKey="geral"
                    title={
                      <span>
                        <GoAlert
                          color="red"
                          size={15}
                          hidden={
                            !(
                              formik.errors.sequencialHomologador ||
                              formik.errors.sequencialSetor ||
                              formik.errors.sequencialTipoAcao ||
                              formik.errors.dataInicialFiscalizacao ||
                              formik.errors.dataFinalFiscalizacao ||
                              formik.errors.textoPadrao ||
                              formik.errors.sequencialCiclo
                            )
                          }
                        />
                        &nbsp;Dados Gerais
                      </span>
                    }
                  >
                    <Row hidden={formik.values.tipoOrigem === ""}>
                      {state.tipoOrigem !== null && (
                        <Form.Group
                          as={Col}
                          sm={12}
                          md={4}
                          hidden={formik?.values.tipoOrigem === "S" || state?.tipoOrigem === "S"}
                        >
                          <Form.Label>
                            <b>{`Nº da ${labelProcedimento}: `}</b>
                            <span data-tip={`Acesse a ${labelProcedimento}`}>
                              <ReactTooltip place="right" type="dark" effect="float" />
                              {state.tipoOrigem !== "P" && (
                                <Link
                                  target="_top"
                                  to={{
                                    pathname: `/app/spf/pendencias/detalhe?itemKey=${state.sequencialOrigem}&origem=${state.tipoOrigem}`,
                                    state: {
                                      itemKey: state.sequencialOrigem,
                                      origem: state.tipoOrigem,
                                    },
                                  }}
                                >
                                  <span data-tip={`Acesse a ${labelProcedimento}`}>{state.sequencialOrigem}</span>
                                </Link>
                              )}

                              {(state.tipoOrigem === "P" || formik.values.tipoOrigem === "P") && (
                                <Link
                                  target="_top"
                                  to={{
                                    pathname: `/app/parteservico/form?itemKey=${state.sequencialOrigem}`,
                                    state: { itemKey: state.sequencialOrigem },
                                  }}
                                >
                                  <span data-tip={`Acesse a ${labelProcedimento} Nº ${state.sequencialOrigem}`}>
                                    {state.sequencialOrigem}
                                  </span>
                                </Link>
                              )}
                            </span>
                          </Form.Label>

                          <Form.Label className="ml-2">
                            {ehParteServico &&
                              (dataDownload?.result[0].ticket !== null ? (
                                <>
                                  <FileDownloader
                                    binario={dataDownload?.result[0].documento}
                                    tooltip={`Visualizar ${labelProcedimento} ${state.sequencialOrigem}`}
                                    className="ml-2"
                                    nomeArquivo={nomeArquivo}
                                  />{" "}
                                </>
                              ) : (
                                <ButtonImagePermission
                                  permission={funcionalidades.spf.download}
                                  className="btn-lg text-success pa-0 p-0"
                                  buttonImage={<BsDownload size="16" color="#e37023" />}
                                  tooltipImage={`Visualizar Documento ${state.sequencialOrigem}`}
                                  onClick={() => exibirDocumento(state.tipoOrigem, state.sequencialOrigem)}
                                />
                              ))}

                            {!ehParteServico && (
                              <FileDownloader
                                binario={dataDownload}
                                tooltip={`Visualizar ${labelProcedimento} ${state.sequencialOrigem}`}
                                className="ml-2"
                                nomeArquivo={nomeArquivo}
                              />
                            )}
                          </Form.Label>
                        </Form.Group>
                      )}
                      <Form.Group as={Col} sm={12} md={4} hidden={formik.values.tipoOrigem !== "S"}>
                        <Form.Label>Número SEI</Form.Label>
                        <TextInputFormik formik={formik} field="numeroSei" />
                      </Form.Group>
                    </Row>
                    <Row>
                      <Form.Group as={Col} sm={12} md={4}>
                        <Form.Label>Homologador</Form.Label>
                        <SelectInputCachedFormik
                          formik={formik}
                          field="sequencialHomologador"
                          tipoItem="homologadores"
                        />
                      </Form.Group>
                      <Form.Group as={Col} sm={12} md={4}>
                        <Form.Label>Ciclo</Form.Label>
                        <SelectInputCachedFormik formik={formik} field="sequencialCiclo" tipoItem="ciclos" />
                      </Form.Group>
                      <Form.Group as={Col} sm={12} md={4}>
                        <Form.Label>Setor</Form.Label>
                        <SelectInputCachedFormik formik={formik} field="sequencialSetor" tipoItem="orgaos" />
                      </Form.Group>
                    </Row>
                    <br />
                    <Row>
                      <Form.Group as={Col} sm={12} md={4}>
                        <Form.Label>Tipo de Ação Fiscal</Form.Label>
                        <SelectInputCachedFormik
                          formik={formik}
                          field="sequencialTipoAcao"
                          tipoItem="tipo-acao-fiscal"
                        />
                      </Form.Group>
                      <Form.Group className="form-group-periodos" as={Col} sm={12} md={5}>
                        <Form.Label>Período de Fiscalização</Form.Label>
                        <div className="form-inline">
                          <DateTimeInputFormik formik={formik} field="dataInicialFiscalizacao" />
                          <Form.Label className="mx-3"> a </Form.Label>
                          <DateTimeInputFormik formik={formik} field="dataFinalFiscalizacao" />
                        </div>
                      </Form.Group>
                    </Row>
                    <br />
                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <CheckBoxProcedimentosFormik formik={formik} field="procedimentos" />
                      </Form.Group>
                    </Row>
                    <br />
                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <Form.Label>Texto Complementar</Form.Label>
                        <HtmlEditor formik={formik} field="textoComplementar" />
                      </Form.Group>
                    </Row>
                    <Row>
                      <Col>
                        <hr />
                        <Form.Label>
                          <strong>Texto da SPF</strong>
                          <BiEdit
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title={showTextoSpf ? "Editar Texto" : "Fechar Edição"}
                            onClick={() => {
                              setShowTextoSpf(!showTextoSpf);
                            }}
                          />
                          <VscReplace
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title="Visualizar texto com substituições"
                            onClick={() => {
                              setShowViewSubstituicao(true);
                              setTextoBody(
                                formik.values.textoComplementar
                                  ? formik.values.textoPadrao.replace("[TEXTO]", formik?.values.textoComplementar)
                                  : formik.values.textoPadrao,
                              );
                            }}
                          />
                        </Form.Label>
                        {!showTextoSpf && <HtmlEditor formik={formik} field="textoPadrao" rows="5" />}
                        <hr hidden={!showTextoSpf} />
                        <br />
                      </Col>
                    </Row>
                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <Form.Label>Anexos</Form.Label>
                        <br />
                        <SelectFileInput
                          id="tab-anexos-arquivos"
                          field="anexos"
                          formik={formik}
                          accept=".jpeg, .pdf, .xls, .xlsx, .doc, .docx"
                          multiple
                          save
                        />
                      </Form.Group>
                      {isloadingAnexo ? (
                        <div>
                          <Spinner animation="border" role="status" />
                          <br />
                          &nbsp;Carregando informações de anexos...
                        </div>
                      ) : (
                        <Form.Group as={Col} sm={12} md={12}>
                          <AnexosSpf
                            anexos={anexos}
                            sqAberturaProcesso={anexos[0]?.sequencialAberturaProcesso}
                            permissao
                          />
                        </Form.Group>
                      )}
                    </Row>
                  </Tab>

                  <Tab
                    eventKey="contribuinte"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.contribuintes} />
                        &nbsp;Contribuintes
                      </span>
                    }
                  >
                    <ListaContribuinteAcaoFiscal
                      formik={formik}
                      field="contribuintes"
                      documento={documentoContribuinte}
                    />
                  </Tab>
                </Tabs>

                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Salvando Solicitação de Procedimento Fiscal..." />
                    ) : (
                      <Button name="Salvar" className="mr-3" onClick={formik.handleSubmit}>
                        Salvar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.numeroAberturaProcesso && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" className="mr-3" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* fim da row */}
    </>
  );
};

export default FormProcedimentoFiscal;
